/**
 * ToneSelector — horizontal scrollable pill row for picking one of the 10
 * editorial tones. Automatically applies the sensible default tone for the
 * active contentType whenever the parent hasn't set one yet.
 *
 * Props:
 *   contentType  String   — PromptRegistry key, used to look up the default tone
 *   value        String   — currently selected tone id (may be empty/undefined)
 *   onChange     fn(id)   — called when the user picks a tone, or when the
 *                           default is auto-applied on mount / contentType change
 */

import React, { useEffect, useState } from 'react';
import { TONES, DEFAULT_TONE_BY_CONTENT_TYPE } from '../../constants/tones';

export const ToneSelector = ({ contentType, value, onChange }) => {
  const [hoveredId, setHoveredId] = useState(null);

  // Auto-apply the default tone whenever value is empty — covers both:
  //   • first mount (value starts as '')
  //   • tab / content-type switches where parent resets tone to '' at the same
  //     time as changing contentType; without `value` in deps the old non-empty
  //     value is captured by the effect closure and !value never triggers.
  useEffect(() => {
    if (!value) {
      const defaultTone = DEFAULT_TONE_BY_CONTENT_TYPE[contentType] || 'authoritative';
      onChange(defaultTone);
    }
  }, [contentType, value]);

  return (
    <div className="relative">
      {/* Responsive tone-chip layout (FIX F, item 9):
            Mobile  (< 768px) : 2-column grid
            Tablet  (768–1023px): 3–4 column grid
            Desktop (>= 1024px) : single row, horizontal scroll if needed
            Ultra-wide (>= 2000px): single row, all chips visible, wider gaps */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-none lg:flex lg:flex-nowrap gap-1.5 3xl:gap-3 lg:overflow-x-auto lg:pb-1"
        style={{ scrollbarWidth: 'thin' }}
      >
        {TONES.map((tone) => {
          const isSelected = value === tone.id;
          return (
            <button
              key={tone.id}
              type="button"
              onClick={() => onChange(tone.id)}
              onMouseEnter={() => setHoveredId(tone.id)}
              onMouseLeave={() => setHoveredId((id) => (id === tone.id ? null : id))}
              title={tone.description}
              className={`relative flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                isSelected
                  ? 'bg-primary-500 border-primary-500 text-white shadow-sm'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
              }`}
            >
              <span aria-hidden="true">{tone.emoji}</span>
              <span>{tone.label}</span>

              {hoveredId === tone.id && (
                <span
                  role="tooltip"
                  className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-48 z-30 px-2.5 py-1.5 rounded-lg bg-gray-900 text-white text-[11px] leading-snug font-normal text-center shadow-lg pointer-events-none"
                >
                  {tone.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ToneSelector;
